/**
 * @file DataTable 数据表格
 * @description 基于 Table 的列配置增强：排序、行选择、固定列、单元格行列合并、加载与空态、底部分页；滚动条经 Table → ScrollArea。
 *
 * @whenToUse 后台列表页主表。
 * @whenNotToUse 静态简单表（直接用 Table 组合）。
 */

import { useMemo, useState, type ReactNode } from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import { cn } from '@/lib/cn.ts'
import { Pagination } from '@/components/ui/pagination.tsx'
import { Spinner } from '@/components/ui/spinner.tsx'
import {
  TABLE_SELECTION_WIDTH,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
  TableSelectionCell,
  TableSelectionHead,
  computeStickyOffsets,
  type TableFixed,
} from '@/components/ui/table.tsx'

/** 未声明 width 的固定列默认宽度 */
const DEFAULT_FIXED_WIDTH = 120

/** 单元格合并：`rowSpan` / `colSpan` 为 0 时不渲染该格 */
export type DataTableCellSpan = {
  /** 纵向合并行数；0 表示被上方单元格覆盖 */
  rowSpan?: number
  /** 横向合并列数；0 表示被左侧单元格覆盖 */
  colSpan?: number
}

export type DataTableColumn<T> = {
  /** 列键（用于 React key / 排序字段名） */
  key: string
  /** 表头 */
  title: ReactNode
  /** 默认取值字段；无 render 时使用 */
  dataIndex?: keyof T
  /** 单元格渲染 */
  render?: (record: T, index: number) => ReactNode
  /**
   * 单元格合并（行合并 / 列合并）。
   * @param record 当前行
   * @param index 当前行在渲染数据中的下标
   * @param data 当前渲染数据（排序后）
   */
  onCell?: (
    record: T,
    index: number,
    data: T[],
  ) => DataTableCellSpan | undefined
  /** 是否可排序（按 dataIndex 或 key 对应字段） */
  sortable?: boolean
  /** 列宽 class，如 w-40（非固定列可用） */
  className?: string
  /** 固定列：left / right；多列同侧时按声明顺序累加偏移 */
  fixed?: TableFixed
  /** 固定列宽 px；固定列用于 sticky 偏移，未写时默认 120 */
  width?: number
  /** 最小列宽 px */
  minWidth?: number
  /** 最大列宽 px */
  maxWidth?: number
  /**
   * 是否开启内容缩略：单行省略，悬停 Tooltip 展示全文。
   * 未开启时按换行正常展示。
   */
  ellipsis?: boolean
}

/**
 * 按连续相同 key 计算 rowSpan：组首为组长度，组内后续为 0。
 * @param data 渲染数据
 * @param index 当前下标
 * @param getKey 分组键
 */
export function computeConsecutiveRowSpan<T>(
  data: T[],
  index: number,
  getKey: (row: T) => string,
): number {
  const key = getKey(data[index])
  if (index > 0 && getKey(data[index - 1]) === key) return 0
  let span = 1
  for (let i = index + 1; i < data.length; i++) {
    if (getKey(data[i]) !== key) break
    span++
  }
  return span
}

export type DataTableProps<T extends { id: string }> = {
  columns: DataTableColumn<T>[]
  data: T[]
  /** 加载中 */
  loading?: boolean
  /** 行选择；启用后选择列默认 fixed left */
  rowSelection?: {
    selectedRowKeys: string[]
    onChange: (keys: string[]) => void
  }
  /** 分页；不传则不分页 */
  pagination?: {
    page: number
    pageSize: number
    total: number
    onChange: (page: number) => void
  }
  /** 行点击 */
  onRowClick?: (record: T, index: number) => void
  /** 行 className */
  rowClassName?: (record: T, index: number) => string | undefined
  className?: string
}

type SortState = {
  key: string
  order: 'asc' | 'desc'
} | null

type ColumnMeta<T> = DataTableColumn<T> & {
  stickyOffset: number
  fixedEdge: boolean
  resolvedWidth: number | undefined
  resolvedMinWidth: number | undefined
  resolvedMaxWidth: number | undefined
}

function readField<T extends { id: string }>(
  record: T,
  dataIndex: keyof T | undefined,
): string {
  if (dataIndex === undefined) return ''
  const value = record[dataIndex]
  if (value == null) return ''
  return String(value)
}

/**
 * 重排列并计算 sticky 偏移：左固定 → 普通 → 右固定
 */
function buildColumnMeta<T>(
  columns: DataTableColumn<T>[],
  pinSelection: boolean,
): {
  ordered: ColumnMeta<T>[]
  selection: { stickyOffset: number; fixedEdge: boolean } | null
} {
  const left = columns.filter((c) => c.fixed === 'left')
  const middle = columns.filter((c) => !c.fixed)
  const right = columns.filter((c) => c.fixed === 'right')

  const leftWidths = left.map((c) => c.width ?? DEFAULT_FIXED_WIDTH)
  const selectionWidth = pinSelection ? TABLE_SELECTION_WIDTH : 0
  const leftOffsets = computeStickyOffsets(
    pinSelection ? [selectionWidth, ...leftWidths] : leftWidths,
  )

  let selectionMeta: { stickyOffset: number; fixedEdge: boolean } | null = null
  let leftOffsetStart = 0
  if (pinSelection) {
    selectionMeta = {
      stickyOffset: leftOffsets[0] ?? 0,
      fixedEdge: left.length === 0,
    }
    leftOffsetStart = 1
  }

  const leftMeta: ColumnMeta<T>[] = left.map((col, index) => ({
    ...col,
    stickyOffset: leftOffsets[leftOffsetStart + index] ?? 0,
    fixedEdge: index === left.length - 1,
    resolvedWidth: col.width ?? DEFAULT_FIXED_WIDTH,
    resolvedMinWidth: col.minWidth,
    resolvedMaxWidth: col.maxWidth,
  }))

  const middleMeta: ColumnMeta<T>[] = middle.map((col) => ({
    ...col,
    stickyOffset: 0,
    fixedEdge: false,
    resolvedWidth: col.width,
    resolvedMinWidth: col.minWidth,
    resolvedMaxWidth: col.maxWidth,
  }))

  const rightWidths = [...right]
    .reverse()
    .map((c) => c.width ?? DEFAULT_FIXED_WIDTH)
  const rightOffsetsRtl = computeStickyOffsets(rightWidths)
  const rightMeta: ColumnMeta<T>[] = right.map((col, index) => {
    const rtlIndex = right.length - 1 - index
    return {
      ...col,
      stickyOffset: rightOffsetsRtl[rtlIndex] ?? 0,
      fixedEdge: index === 0,
      resolvedWidth: col.width ?? DEFAULT_FIXED_WIDTH,
      resolvedMinWidth: col.minWidth,
      resolvedMaxWidth: col.maxWidth,
    }
  })

  return {
    ordered: [...leftMeta, ...middleMeta, ...rightMeta],
    selection: selectionMeta,
  }
}

export function DataTable<T extends { id: string }>({
  columns,
  data,
  loading = false,
  rowSelection,
  pagination,
  onRowClick,
  rowClassName,
  className,
}: DataTableProps<T>) {
  const [sort, setSort] = useState<SortState>(null)

  const pinSelection = Boolean(rowSelection)

  const { ordered, selection } = useMemo(
    () => buildColumnMeta(columns, pinSelection),
    [columns, pinSelection],
  )

  const sorted = useMemo(() => {
    if (!sort) return data
    const col = ordered.find((c) => c.key === sort.key)
    if (!col?.dataIndex) return data
    const index = col.dataIndex
    const next = [...data]
    next.sort((a, b) => {
      const cmp = readField(a, index).localeCompare(readField(b, index), 'zh', {
        numeric: true,
      })
      return sort.order === 'asc' ? cmp : -cmp
    })
    return next
  }, [ordered, data, sort])

  const allIds = sorted.map((row) => row.id)
  const selected = rowSelection?.selectedRowKeys ?? []
  const allSelected =
    allIds.length > 0 && allIds.every((id) => selected.includes(id))
  const someSelected = selected.length > 0 && !allSelected
  const colSpan = ordered.length + (rowSelection ? 1 : 0)

  const toggleSort = (key: string) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, order: 'asc' }
      if (prev.order === 'asc') return { key, order: 'desc' }
      return null
    })
  }

  const selectionFixed = pinSelection ? ('left' as const) : undefined
  /** 刷新加载：保留已有行，仅盖轻蒙层；首屏无数据时才展示完整 loading */
  const hasRows = sorted.length > 0

  return (
    <div className={cn('space-y-3 flex flex-col flex-1 overflow-hidden', className)}>
      <div className="relative rounded-lg flex-1 flex flex-col overflow-hidden" aria-busy={loading || undefined}>
        <Table scrollerClassName="min-h-0 min-w-0 flex-1">
          <TableHeader className="sticky top-0 z-11">
            <TableRow>
              {rowSelection ? (
                <TableSelectionHead
                  checked={allSelected}
                  indeterminate={someSelected}
                  fixed={selectionFixed}
                  stickyOffset={selection?.stickyOffset ?? 0}
                  fixedEdge={selection?.fixedEdge ?? false}
                  onCheckedChange={(checked) => {
                    rowSelection.onChange(checked ? allIds : [])
                  }}
                />
              ) : null}
              {ordered.map((column) => (
                <TableHead
                  key={column.key}
                  className={column.className}
                  fixed={column.fixed}
                  stickyOffset={column.stickyOffset}
                  fixedEdge={column.fixedEdge}
                  width={column.resolvedWidth}
                  minWidth={column.resolvedMinWidth}
                  maxWidth={column.resolvedMaxWidth}
                  ellipsis={column.ellipsis}
                >
                  {column.sortable ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 transition-opacity duration-150 hover:opacity-90"
                      onClick={() => toggleSort(column.key)}
                    >
                      {column.title}
                      {sort?.key === column.key ? (
                        sort.order === 'asc' ? (
                          <ArrowUp className="size-3.5" aria-hidden />
                        ) : (
                          <ArrowDown className="size-3.5" aria-hidden />
                        )
                      ) : (
                        <ArrowUpDown
                          className="size-3.5 text-muted"
                          aria-hidden
                        />
                      )}
                    </button>
                  ) : (
                    column.title
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.length === 0 ? (
              <TableEmpty colSpan={colSpan} />
            ) : (
              sorted.map((record, index) => (
                <TableRow
                  key={record.id}
                  className={cn(
                    onRowClick && 'cursor-pointer',
                    rowClassName?.(record, index),
                  )}
                  onClick={
                    onRowClick
                      ? () => onRowClick(record, index)
                      : undefined
                  }
                >
                  {rowSelection ? (
                    <TableSelectionCell
                      checked={selected.includes(record.id)}
                      fixed={selectionFixed}
                      stickyOffset={selection?.stickyOffset ?? 0}
                      fixedEdge={selection?.fixedEdge ?? false}
                      onCheckedChange={(checked) => {
                        rowSelection.onChange(
                          checked
                            ? [...selected, record.id]
                            : selected.filter((id) => id !== record.id),
                        )
                      }}
                    />
                  ) : null}
                  {ordered.map((column) => {
                    const cellSpan = column.onCell?.(record, index, sorted)
                    const rowSpan = cellSpan?.rowSpan
                    const colSpan = cellSpan?.colSpan
                    if (rowSpan === 0 || colSpan === 0) return null
                    return (
                      <TableCell
                        key={column.key}
                        className={column.className}
                        fixed={column.fixed}
                        stickyOffset={column.stickyOffset}
                        fixedEdge={column.fixedEdge}
                        width={column.resolvedWidth}
                        minWidth={column.resolvedMinWidth}
                        maxWidth={column.resolvedMaxWidth}
                        ellipsis={column.ellipsis}
                        rowSpan={rowSpan}
                        colSpan={colSpan}
                      >
                        {column.render
                          ? column.render(record, index)
                          : readField(record, column.dataIndex)}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {loading ? (
          <Spinner
            label={hasRows ? '' : '加载中'}
            className="bg-raised/20 backdrop-blur-[2px] rounded-[inherit]"
          />
        ) : null}
      </div>
      {pagination ? (
        <Pagination
          page={pagination.page}
          pageSize={pagination.pageSize}
          total={pagination.total}
          onChange={pagination.onChange}
        />
      ) : null}
    </div>
  )
}
